import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import snapshotLogo from "@/assets/snapshot-logo.png";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);

  useEffect(() => {
    const handleAuth = async () => {
      // Check for PKCE code in URL (Supabase sends this after verification)
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code');
      
      if (code) {
        console.log('🔐 Reset Password - Exchanging code for session');
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('Code exchange error:', error);
            toast({
              variant: "destructive",
              title: "Invalid Reset Link",
              description: error.message || "This link is invalid or has expired."
            });
            return;
          }
          // Code exchanged successfully, check session below
        } catch (err) {
          console.error('Code exchange exception:', err);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to process reset link. Please try again."
          });
          return;
        }
      }

      // Check for sessionStorage data (from two-step landing page)
      const storedData = sessionStorage.getItem('passwordResetData');
      if (storedData) {
        console.log('🔐 Reset Password - Found sessionStorage data, verifying OTP');
        sessionStorage.removeItem('passwordResetData'); // Clean up immediately
        
        try {
          const { token, email } = JSON.parse(storedData);
          
          if (token && email) {
            const { error } = await supabase.auth.verifyOtp({
              token,
              type: 'recovery',
              email
            });
            
            if (error) {
              console.error('verifyOtp error:', error);
              toast({
                variant: "destructive",
                title: "Link Expired",
                description: error.message || "This password reset link has expired. Please request a new one."
              });
              return;
            }
            
            console.log('🔐 Reset Password - OTP verified successfully');
            setHasValidToken(true);
            return;
          }
        } catch (err) {
          console.error('sessionStorage parse error:', err);
        }
      }

      // Check for valid session (from successful code exchange or OTP verification)
      const { data: { session } } = await supabase.auth.getSession();
      
      console.log('🔐 Reset Password Page - Session check:', { 
        hasSession: !!session,
        email: session?.user?.email
      });

      if (session) {
        setHasValidToken(true);
      } else if (!code && !storedData) {
        // Only show error if there was no code or stored data to process
        toast({
          variant: "destructive",
          title: "Invalid Reset Link",
          description: "This password reset link is invalid or has expired. Please request a new one."
        });
      }
    };

    handleAuth();
  }, [toast]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all fields."
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Password must be at least 6 characters long."
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Passwords do not match."
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your password has been reset successfully. Redirecting to dashboard..."
      });

      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to reset password. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestNewLink = () => {
    navigate("/", { state: { openAuthDialog: true, authView: 'forgotten_password' } });
  };

  if (!hasValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center mb-4">
              <BrandLogo fallback={snapshotLogo} />
            </div>
            <CardTitle className="text-2xl">Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleRequestNewLink}
              className="w-full"
            >
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <BrandLogo fallback={snapshotLogo} />
          </div>
          <CardTitle className="text-2xl">Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
                minLength={6}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Resetting Password..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;

function BrandLogo({ fallback }: { fallback: string }) {
  const { brand, isWhiteLabel } = useBrand();
  const logo = isWhiteLabel ? brand?.config.logo_url : null;
  const name = isWhiteLabel ? brand?.config.company_name || "Logo" : "Snapshot Live";
  return <img src={logo || fallback} alt={name} className={isWhiteLabel ? "brand-reset-logo object-contain" : "h-16 w-auto object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />;
}
